import React, { useEffect, useMemo, useRef } from 'react';
import ReactDOMServer from 'react-dom/server';
import { Chessground } from 'chessground';
import { Chess } from 'chess.js';
import { Key } from 'chessground/types';
import { DrawShape } from 'chessground/draw';
import { MoveType } from '../types/MoveType';
import { getIcon } from '../utils/ChessTypes';

// A small view-only board from a FEN, using the global chessground styles. Draws the game overlay's
// annotations: a move-type badge on the played square and a green arrow for the best move.
export default function MiniChessboard(props: {
  fen: string;
  pgn?: string;
  orientation: 'white' | 'black';
  moveType?: MoveType | null;
  parentBestMove?: string | null;
  coordinates?: boolean;
}): React.ReactNode {
  const { fen, pgn, orientation, moveType, parentBestMove, coordinates = false } = props;
  const ref = useRef<HTMLDivElement>(null);

  // The last move, derived from the pgn, so the board shows which move the comment is about.
  const lastMove = useMemo<[Key, Key] | undefined>(() => {
    if (!pgn) {
      return undefined;
    }
    try {
      const chess = new Chess();
      chess.loadPgn(pgn);
      const last = chess.history({ verbose: true }).at(-1);
      return last ? [last.from as Key, last.to as Key] : undefined;
    } catch {
      return undefined;
    }
  }, [pgn]);

  // Move-type badge on the played move's destination, as the overlay draws it.
  const autoShapes = useMemo<DrawShape[]>(() => {
    if (moveType == null || lastMove == null) {
      return [];
    }
    const Icon = getIcon(moveType);
    const html = ReactDOMServer.renderToStaticMarkup(
      <svg width="20" height="20" viewBox="0 0 8 8">
        <Icon />
      </svg>,
    );
    return [{ orig: lastMove[1], customSvg: { html } }];
  }, [moveType, lastMove]);

  // Green arrow for the engine's best move at the previous position.
  const shapes = useMemo<DrawShape[]>(() => {
    if (parentBestMove == null || parentBestMove.length < 4) {
      return [];
    }
    return [
      {
        orig: parentBestMove.slice(0, 2) as Key,
        dest: parentBestMove.slice(2, 4) as Key,
        brush: 'green',
      },
    ];
  }, [parentBestMove]);

  useEffect(() => {
    if (ref.current == null) {
      return;
    }
    const cg = Chessground(ref.current, {
      fen,
      orientation,
      lastMove,
      viewOnly: true,
      coordinates,
      animation: { enabled: false },
      drawable: { enabled: false, visible: true, autoShapes, shapes },
    });
    return () => cg.destroy();
  }, [fen, orientation, lastMove, autoShapes, shapes, coordinates]);

  return <div className="cm-mini-board" ref={ref} />;
}
